/**
 * This component is used around the site to display a donate button
 * which, when clicked, opens our donation page (donate.khanacademy.org) in
 * a new window/tab.
 */
import {t} from "@lingui/core/macro";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {RequestInfo} from "@khan/shared-core";

import {getKaid} from "#data-access/profile-utils.ts";
import useProfile from "#data-access/use-profile.ts";
import getClassyCampaignURL, {
    type ClassyPassThroughParams,
} from "#donate-package/classy.ts";

type Props = {
    children?: string;
    // aphrodite styles
    style?: StyleType;
    // react key
    key?: string;
    // params that get passed-through to classy
    // c_src will default to the users kaid
    urlParams?: ClassyPassThroughParams;
    // Wonderblocks safeWithNav function
    safeWithNav?: () => Promise<unknown>;
    // QA Test id
    testId?: string;
    // for button type
    light?: boolean;
    // Wonderblocks button size: "small", "medium", or "large"
    size?: "medium" | "small" | "large";
};

export default function DonateButton(props: Props): React.ReactElement {
    const {children, urlParams, safeWithNav, ...rest} = props;

    const profileResults = useProfile();

    return (
        <Button
            target="_blank"
            href={getClassyCampaignURL(RequestInfo.kaLocale, {
                c_src: getKaid(profileResults.profile),
                ...urlParams,
            })}
            {...(safeWithNav ? {safeWithNav: safeWithNav} : null)}
            {...rest}
        >
            {children ?? t`Donate`}
        </Button>
    );
}
